import React, { createContext, useContext, useState, useEffect } from 'react';

export const DOCTOR_INFO = {
  name: 'Prof. Dr. Ruqqia Sultana',
  nameUrdu: 'پروفیسر ڈاکٹر رقیہ سلطانہ',
  title: 'GYNAECOLOGIST',
  degrees: ['M.B.B.S', 'F.C.P.S (PAK)', 'M.R.C.O.G (U.K)', 'Master Certification Urogynae (U.K)'],
  pmdc: '6429-N',
  college: 'Ayub Medical College Abbottabad',
  clinicName: 'Dr. Ruqqia Sultana Clinic & Maternity Home',
  address: 'Behind Mubarik Plaza, Opp Muqaddas Tower, Mansehra Road, Mandian, Abbottabad, KPK',
  phone: '0992-412531',
  opdTiming: '3 PM to 8 PM (Mon-Sat)',
  emergency: '24/7 Available',
  holiday: 'Sunday (except emergency)'
};

const DataContext = createContext();

export const useData = () => useContext(DataContext);

const generateId = () => Date.now().toString(36) + Math.random().toString(36).substr(2);

// Demo users with new role structure
const USERS = [
  { username: 'reception', password: 'reception123', role: 'receptionist', name: 'Receptionist' },
  { username: 'admin', password: 'admin123', role: 'admin', name: 'Administrator' },
  { username: 'doctor', password: 'doctor123', role: 'doctor', name: 'Doctor/Nurse' },
  { username: 'lab', password: 'lab123', role: 'lab', name: 'Lab Technician' }
];

export const DataProvider = ({ children }) => {
  // State for all data
  const [patients, setPatients] = useState([]);
  const [birthRecords, setBirthRecords] = useState([]);
  const [bills, setBills] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [news, setNews] = useState([]);
  const [sliderImages, setSliderImages] = useState([]);
  const [medicalRecords, setMedicalRecords] = useState([]);
  // PHC Compliance Data
  const [complaints, setComplaints] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [staffCredentials, setStaffCredentials] = useState([]);
  // New: Baby Medical Records & Lab Reports
  const [babyMedicalRecords, setBabyMedicalRecords] = useState([]);
  const [labReports, setLabReports] = useState([]);
  // Outdoor Patient Prescriptions
  const [outdoorPrescriptions, setOutdoorPrescriptions] = useState([]);

  // Load from localStorage
  useEffect(() => {
    const loadData = (key, setter, defaultVal = []) => {
      const data = localStorage.getItem(key);
      setter(data ? JSON.parse(data) : defaultVal);
    };
    loadData('clinic_patients_v3', setPatients, getDemoPatients());
    loadData('clinic_births_v3', setBirthRecords, getDemoBirths());
    loadData('clinic_bills_v3', setBills, getDemoBills());
    loadData('clinic_transactions_v3', setTransactions, getDemoTransactions());
    loadData('clinic_news_v3', setNews, getDemoNews());
    loadData('clinic_slider_v3', setSliderImages, getDemoSlider());
    loadData('clinic_medical_v3', setMedicalRecords, []);
    // PHC Data
    loadData('clinic_complaints_v3', setComplaints, []);
    loadData('clinic_incidents_v3', setIncidents, []);
    loadData('clinic_staff_v3', setStaffCredentials, getDemoStaff());
    // New Data
    loadData('clinic_baby_medical_v3', setBabyMedicalRecords, []);
    loadData('clinic_lab_reports_v3', setLabReports, []);
    loadData('clinic_outdoor_prescriptions_v3', setOutdoorPrescriptions, []);
  }, []);

  // Save to localStorage
  useEffect(() => { localStorage.setItem('clinic_patients_v3', JSON.stringify(patients)); }, [patients]);
  useEffect(() => { localStorage.setItem('clinic_births_v3', JSON.stringify(birthRecords)); }, [birthRecords]);
  useEffect(() => { localStorage.setItem('clinic_bills_v3', JSON.stringify(bills)); }, [bills]);
  useEffect(() => { localStorage.setItem('clinic_transactions_v3', JSON.stringify(transactions)); }, [transactions]);
  useEffect(() => { localStorage.setItem('clinic_news_v3', JSON.stringify(news)); }, [news]);
  useEffect(() => { localStorage.setItem('clinic_slider_v3', JSON.stringify(sliderImages)); }, [sliderImages]);
  useEffect(() => { localStorage.setItem('clinic_medical_v3', JSON.stringify(medicalRecords)); }, [medicalRecords]);
  // PHC Data Save
  useEffect(() => { localStorage.setItem('clinic_complaints_v3', JSON.stringify(complaints)); }, [complaints]);
  useEffect(() => { localStorage.setItem('clinic_incidents_v3', JSON.stringify(incidents)); }, [incidents]);
  useEffect(() => { localStorage.setItem('clinic_staff_v3', JSON.stringify(staffCredentials)); }, [staffCredentials]);
  // New Data Save
  useEffect(() => { localStorage.setItem('clinic_baby_medical_v3', JSON.stringify(babyMedicalRecords)); }, [babyMedicalRecords]);
  useEffect(() => { localStorage.setItem('clinic_lab_reports_v3', JSON.stringify(labReports)); }, [labReports]);
  useEffect(() => { localStorage.setItem('clinic_outdoor_prescriptions_v3', JSON.stringify(outdoorPrescriptions)); }, [outdoorPrescriptions]);

  // Generate MR Number
  const generateMRNo = () => {
    const year = new Date().getFullYear();
    const count = patients.filter(p => p.mrNo?.includes(year.toString())).length + 1;
    return `MR-${year}-${count.toString().padStart(3, '0')}`;
  };

  // Generate Certificate Number
  const generateCertificateNo = () => {
    const year = new Date().getFullYear();
    const count = birthRecords.filter(b => b.certificateNo?.includes(year.toString())).length + 1;
    return `BC-${year}-${count.toString().padStart(3, '0')}`;
  };

  // Auth
  const authenticateUser = (username, password) => USERS.find(u => u.username === username && u.password === password);

  // Patient CRUD
  const addPatient = (patient) => {
    const newPatient = { ...patient, id: generateId(), mrNo: generateMRNo(), admissionDate: new Date().toISOString().split('T')[0], admissionTime: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }), status: 'admitted', dischargeRequested: false, dischargeApproved: false };
    setPatients(prev => [...prev, newPatient]);
    return newPatient;
  };
  const updatePatient = (id, data) => setPatients(prev => prev.map(p => p.id === id ? { ...p, ...data } : p));
  const dischargePatient = (id) => setPatients(prev => prev.map(p => p.id === id ? { ...p, status: 'discharged', dischargeDate: new Date().toISOString().split('T')[0], dischargeTime: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) } : p));
  
  // Request discharge (doctor requests, admin approves)
  const requestDischarge = (id, data = {}) => setPatients(prev => prev.map(p => p.id === id ? { ...p, ...data, dischargeRequested: true } : p));
  const approveDischarge = (id) => {
    // First update patient status
    setPatients(prev => prev.map(p => p.id === id ? { 
      ...p, 
      dischargeApproved: true, 
      status: 'discharged', 
      dischargeDate: new Date().toISOString().split('T')[0], 
      dischargeTime: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
      dischargeApprovedAt: new Date().toISOString()
    } : p));
  };
  const cancelDischargeRequest = (id) => setPatients(prev => prev.map(p => p.id === id ? { ...p, dischargeRequested: false, dischargeApproved: false, dischargePatientCondition: '', dischargeBabyCondition: '' } : p));

  // Get patient dues
  const getPatientDues = (patientId) => {
    const patientBills = bills.filter(b => b.patientId === patientId);
    const totalBilled = patientBills.reduce((sum, b) => sum + Number(b.totalAmount || 0), 0);
    const totalPaid = patientBills.reduce((sum, b) => sum + Number(b.paidAmount || 0), 0);
    return totalBilled - totalPaid;
  };

  // Birth Records CRUD
  const addBirthRecord = (record) => {
    const now = new Date();
    const newRecord = { 
      ...record, 
      id: generateId(), 
      certificateNo: generateCertificateNo(),
      daycareAdmissionTime: now.toISOString(),
      daycareStatus: 'in-daycare', // in-daycare, with-mother, in-nursery
      nurseryAdmissionTime: null,
      nurseryForm: null
    };
    setBirthRecords(prev => [...prev, newRecord]);
    return newRecord;
  };
  const updateBirthRecord = (id, data) => setBirthRecords(prev => prev.map(b => b.id === id ? { ...b, ...data } : b));

  // Bills CRUD
  const addBill = (bill) => {
    const newBill = { ...bill, id: generateId(), date: new Date().toISOString().split('T')[0] };
    setBills(prev => [...prev, newBill]);
    return newBill;
  };
  const updateBill = (id, data) => setBills(prev => prev.map(b => b.id === id ? { ...b, ...data } : b));

  // Transactions CRUD
  const addTransaction = (transaction) => {
    const newTransaction = { ...transaction, id: generateId(), date: transaction.date || new Date().toISOString().split('T')[0] };
    setTransactions(prev => [...prev, newTransaction]);
    return newTransaction;
  };

  // News CRUD
  const addNews = (item) => {
    const newItem = { ...item, id: generateId(), date: new Date().toISOString().split('T')[0] };
    setNews(prev => [...prev, newItem]);
    return newItem;
  };
  const updateNews = (id, data) => setNews(prev => prev.map(n => n.id === id ? { ...n, ...data } : n));
  const deleteNews = (id) => setNews(prev => prev.filter(n => n.id !== id));

  // Slider Images CRUD
  const addSliderImage = (image) => {
    const newImage = { ...image, id: generateId() };
    setSliderImages(prev => [...prev, newImage]);
    return newImage;
  };
  const updateSliderImage = (id, data) => setSliderImages(prev => prev.map(s => s.id === id ? { ...s, ...data } : s));
  const deleteSliderImage = (id) => setSliderImages(prev => prev.filter(s => s.id !== id));

  // Medical Records CRUD
  const addMedicalRecord = (record) => {
    const newRecord = { ...record, id: generateId(), date: new Date().toISOString().split('T')[0], time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) };
    setMedicalRecords(prev => [...prev, newRecord]);
    return newRecord;
  };
  const updateMedicalRecord = (id, data) => setMedicalRecords(prev => prev.map(m => m.id === id ? { ...m, ...data } : m));

  // Stats
  const getStats = () => {
    const totalIncome = transactions.filter(t => t.type === 'income').reduce((sum, t) => sum + Number(t.amount), 0);
    const totalExpenses = transactions.filter(t => t.type === 'expense').reduce((sum, t) => sum + Number(t.amount), 0);
    const pendingBills = bills.filter(b => b.status !== 'paid').reduce((sum, b) => sum + (Number(b.totalAmount) - Number(b.paidAmount || 0)), 0);
    return {
      totalPatients: patients.length,
      admittedPatients: patients.filter(p => p.status === 'admitted').length,
      totalDeliveries: birthRecords.length,
      totalIncome, totalExpenses,
      netBalance: totalIncome - totalExpenses,
      pendingBills
    };
  };

  // Reset Password (for admin)
  const resetPassword = (username, newPassword) => {
    const userIndex = USERS.findIndex(u => u.username === username);
    if (userIndex !== -1) {
      USERS[userIndex].password = newPassword;
      return true;
    }
    return false;
  };

  // PHC Complaints Management
  const generateComplaintId = () => {
    const year = new Date().getFullYear();
    const count = complaints.filter(c => c.id?.includes(year.toString())).length + 1;
    return `CMP-${year}-${count.toString().padStart(3, '0')}`;
  };

  const addComplaint = (complaint) => setComplaints([...complaints, { ...complaint, id: generateComplaintId() }]);
  const updateComplaint = (id, updates) => setComplaints(complaints.map(c => c.id === id ? { ...c, ...updates } : c));

  // PHC Incidents Management
  const generateIncidentId = () => {
    const year = new Date().getFullYear();
    const count = incidents.filter(i => i.id?.includes(year.toString())).length + 1;
    return `INC-${year}-${count.toString().padStart(3, '0')}`;
  };

  const addIncident = (incident) => setIncidents([...incidents, { ...incident, id: generateIncidentId() }]);

  // PHC Staff Credentials
  const addStaffCredential = (staff) => setStaffCredentials([...staffCredentials, { ...staff, id: generateId() }]);
  const updateStaffCredential = (id, updates) => setStaffCredentials(staffCredentials.map(s => s.id === id ? { ...s, ...updates } : s));

  // Baby Medical Records
  const addBabyMedicalRecord = (record) => setBabyMedicalRecords([...babyMedicalRecords, { ...record, id: generateId() }]);

  // Lab Reports
  const generateLabReportId = () => {
    const year = new Date().getFullYear();
    const count = labReports.filter(r => r.id?.includes(year.toString())).length + 1;
    return `LAB-${year}-${count.toString().padStart(4, '0')}`;
  };
  const addLabReport = (report) => setLabReports([...labReports, { ...report, id: generateLabReportId() }]);
  const deleteLabReport = (id) => setLabReports(labReports.filter(r => r.id !== id));

  // Outdoor Patient Prescriptions
  const generatePrescriptionNo = () => {
    const year = new Date().getFullYear();
    const count = outdoorPrescriptions.filter(p => p.prescriptionNo?.includes(year.toString())).length + 1;
    return `OPD-${year}-${count.toString().padStart(4, '0')}`;
  };
  const addOutdoorPrescription = (prescription) => {
    const newPrescription = {
      ...prescription,
      id: generateId(),
      prescriptionNo: generatePrescriptionNo(),
      date: new Date().toISOString().split('T')[0],
      time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })
    };
    setOutdoorPrescriptions([...outdoorPrescriptions, newPrescription]);
    return newPrescription;
  };
  const deleteOutdoorPrescription = (id) => setOutdoorPrescriptions(outdoorPrescriptions.filter(p => p.id !== id));

  // Clear all data
  const clearAllData = () => {
    setPatients([]);
    setBirthRecords([]);
    setBills([]);
    setTransactions([]);
    setNews([]);
    setSliderImages([]);
    setMedicalRecords([]);
    setComplaints([]);
    setIncidents([]);
    setStaffCredentials([]);
    setBabyMedicalRecords([]);
    setLabReports([]);
    setOutdoorPrescriptions([]);
  };

  const value = {
    patients, birthRecords, bills, transactions, news, sliderImages, medicalRecords,
    complaints, incidents, staffCredentials, babyMedicalRecords, labReports, outdoorPrescriptions,
    addPatient, updatePatient, dischargePatient, requestDischarge, approveDischarge, cancelDischargeRequest, getPatientDues,
    addBirthRecord, updateBirthRecord,
    addBill, updateBill,
    addTransaction,
    addNews, updateNews, deleteNews,
    addSliderImage, updateSliderImage, deleteSliderImage,
    addMedicalRecord, updateMedicalRecord,
    addComplaint, updateComplaint,
    addIncident,
    addStaffCredential, updateStaffCredential,
    addBabyMedicalRecord,
    addLabReport, deleteLabReport,
    addOutdoorPrescription, deleteOutdoorPrescription,
    authenticateUser, getStats, generateMRNo, generateCertificateNo,
    resetPassword, clearAllData, USERS
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};

// Demo Data
function getDemoPatients() {
  return [
   ];
}

function getDemoBirths() {
  return [
  ];
}

function getDemoBills() {
  return [
  ];
}

function getDemoTransactions() {
  return [
 
  ];
}

function getDemoNews() {
  return [
  ];
}

function getDemoSlider() {
  return [
    { id: '1', imageUrl: 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=800', title: 'Expert Maternity Care', description: 'Comprehensive care for mother and baby', active: true },
    { id: '2', imageUrl: 'https://images.unsplash.com/photo-1551601651-2a8555f1a136?w=800', title: 'Advanced Gynecology Services', description: 'Modern surgical procedures with care', active: true },
    { id: '3', imageUrl: 'https://images.unsplash.com/photo-1538108149393-fbbd81895907?w=800', title: '24/7 Emergency Services', description: 'Always here when you need us', active: true }
  ];
}

function getDemoStaff() {
  return [
  ];
}

export default DataContext;
